import type { IDLProduct, IDLEcommerceEvent, IDLCheckoutEvent, IDLWishlistEvent, IDLPurchaseActionField } from '~/types/dataLayer';

export const useDataLayer = () => {
  const ymCounterIdRaw = useRuntimeConfig().public.YM_COUNTER_ID;
  const ymCounterId = Number(ymCounterIdRaw);
  const push = (data: IDLEcommerceEvent | IDLCheckoutEvent | Record<string, unknown>) => {
    if (!import.meta.client) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  };

  const impressions = (products: IDLProduct[]) => {
    push({ ecommerce: { currencyCode: 'RUB', impressions: products } });
  };

  const click = (product: IDLProduct) => {
    push({ ecommerce: { currencyCode: 'RUB', click: { products: [product] } } });
  };

  const detail = (product: IDLProduct) => {
    push({ ecommerce: { currencyCode: 'RUB', detail: { products: [product] } } });
  };

  const addToCart = (product: IDLProduct) => {
    push({ ecommerce: { currencyCode: 'RUB', add: { products: [product] } } });
  };

  const removeFromCart = (product: IDLProduct) => {
    push({ ecommerce: { currencyCode: 'RUB', remove: { products: [product] } } });
  };

  const purchase = (
    actionField: IDLPurchaseActionField,
    products: IDLProduct[],
    orderId: string,
  ) => {
    if (!import.meta.client) return;
    const key = `dl_purchase_${orderId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    push({ ecommerce: { currencyCode: 'RUB', purchase: { actionField, products } } });
  };

  const checkoutOpen = (products: IDLProduct[]) => {
    push({
      event: 'checkout_open',
      checkout: { step: 1, step_name: 'Открытие страницы оформления заказа', products },
    });
  };

  const checkoutDeliveryFilled = (products: IDLProduct[], shippingTier: string) => {
    push({
      event: 'checkout_delivery_filled',
      checkout: { step: 2, step_name: 'Заполнение данных и выбор доставки', shipping_tier: shippingTier, products },
    });
  };

  const checkoutFittingSelected = (products: IDLProduct[], fittingType: string) => {
    push({
      event: 'checkout_fitting_selected',
      checkout: { step: 3, step_name: 'Выбор примерки', fitting_type: fittingType, products },
    });
  };

  const checkoutPaymentSelected = (products: IDLProduct[], paymentType: string) => {
    push({
      event: 'checkout_payment_selected',
      checkout: { step: 4, step_name: 'Выбор способа оплаты', payment_type: paymentType, products },
    });
  };

  const addToWishlist = (data: IDLWishlistEvent) => {
    if (!import.meta.client) return;
    if (!ymCounterId) {
      console.error('[useDataLayer] YM_COUNTER_ID не задан в переменных окружения');
      return;
    }
    window.ym?.(ymCounterId, 'reachGoal', 'add_to_wishlist', data);
  };

  return {
    impressions,
    click,
    detail,
    addToCart,
    removeFromCart,
    purchase,
    checkoutOpen,
    checkoutDeliveryFilled,
    checkoutFittingSelected,
    checkoutPaymentSelected,
    addToWishlist,
  };
};
